import { getFolderFromRawId } from '../../utils/folderTypeConversionUtil';
import getActionClauseTypeOptions from './getActionClauseTypeOptions';
import getConditionalExceptionClauseTypeOptions from './getConditionalExceptionClauseTypeOptions';
import getExistingClauseTypesInSection from './getExistingClauseTypesInSection';
import mapReadWriteRecipientWellViewStateToPeopleIdentity from './mapReadWriteRecipientWellViewStateToPeopleIdentity';
import ClauseValueType, { KB_TO_B } from '../../store/schema/ClauseValueType';
import InboxRuleFolderType from '../../store/schema/InboxRuleFolderType';
import store from '../../store/store';
import getDefaultInboxRuleClauseValue from 'owa-inbox-rules-option/lib/actions/helpers/getDefaultInboxRuleClauseValue';
import InboxRule from 'owa-service/lib/contract/InboxRule';
import NullableImportance from 'owa-service/lib/contract/NullableImportance';
import NullableInboxRuleMessageFlag from 'owa-service/lib/contract/NullableInboxRuleMessageFlag';
import NullableInboxRuleMessageType from 'owa-service/lib/contract/NullableInboxRuleMessageType';
import NullableSensitivity from 'owa-service/lib/contract/NullableSensitivity';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';

/**
 * Maps the rule clauses from the advanced rules option page to an inbox rule
 * @return the InboxRule with the corresponding clauses as properties
 */
export default function createInboxRuleFromStoreEditState(inboxRuleId?: string): InboxRule {
    let inboxRule = <InboxRule>{};
    let editState = store.inboxRulesEditState;
    let clauseMap = editState.clauseMap;
    inboxRule.Name = editState.ruleName;
    let conditionalExistingClauseTypesMap = getExistingClauseTypesInSection('conditional');
    let actionExistingClauseTypesMap = getExistingClauseTypesInSection('action');
    let exceptionExistingClauseTypesMap = getExistingClauseTypesInSection('exception');
    let originalRule = inboxRuleId ? store.inboxRulesListState.inboxRules.get(inboxRuleId) : null;

    // map conditional and exception clauses
    for (let conditionalExceptionClauseType of getConditionalExceptionClauseTypeOptions()) {
        let conditionalClauseId = conditionalExistingClauseTypesMap.get(
            conditionalExceptionClauseType
        );
        let exceptionClauseId = exceptionExistingClauseTypesMap.get(conditionalExceptionClauseType);
        let conditionalClauseValue = conditionalClauseId
            ? clauseMap.get(conditionalClauseId).clauseValue
            : null;
        let exceptionClauseValue = exceptionClauseId
            ? clauseMap.get(exceptionClauseId).clauseValue
            : null;
        let defaultValue = getDefaultInboxRuleClauseValue(conditionalExceptionClauseType);
        let originalConditionalValue;
        let originalExceptionValue;
        let newConditionalValue;
        let newExceptionValue;

        // In this switch statement, we decide whether to set the value of the clause type to be sent to server by the following ways:
        // 1) If there is no new value and there is no old value, do nothing (means user never has this clause type in the rule)
        // 2) Otherwise, get clause value

        // Otherwise do not set because the value has not changed
        switch (conditionalExceptionClauseType) {
            case 'from':
                originalConditionalValue = originalRule ? originalRule.From : defaultValue;
                originalExceptionValue = originalRule ? originalRule.ExceptIfFrom : defaultValue;
                newConditionalValue = getClauseValueWithFallback(
                    conditionalClauseValue,
                    mapReadWriteRecipientWellViewStateToPeopleIdentity,
                    defaultValue
                );
                newExceptionValue = getClauseValueWithFallback(
                    exceptionClauseValue,
                    mapReadWriteRecipientWellViewStateToPeopleIdentity,
                    defaultValue
                );

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.From = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfFrom = value;
                    }
                );
                break;
            case 'to':
                originalConditionalValue = originalRule ? originalRule.SentTo : defaultValue;
                originalExceptionValue = originalRule ? originalRule.ExceptIfSentTo : defaultValue;
                newConditionalValue = getClauseValueWithFallback(
                    conditionalClauseValue,
                    mapReadWriteRecipientWellViewStateToPeopleIdentity,
                    defaultValue
                );
                newExceptionValue = getClauseValueWithFallback(
                    exceptionClauseValue,
                    mapReadWriteRecipientWellViewStateToPeopleIdentity,
                    defaultValue
                );

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.SentTo = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfSentTo = value;
                    }
                );
                break;
            case 'onTheToOrCcLine':
                originalConditionalValue = originalRule
                    ? originalRule.MyNameInToOrCcBox
                    : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfMyNameInToOrCcBox
                    : defaultValue;
                newConditionalValue = conditionalClauseId ? true : defaultValue;
                newExceptionValue = exceptionClauseId ? true : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.MyNameInToOrCcBox = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfMyNameInToOrCcBox = value;
                    }
                );
                break;
            case 'theOnlyRecipientListed':
                originalConditionalValue = originalRule ? originalRule.SentOnlyToMe : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfSentOnlyToMe
                    : defaultValue;
                newConditionalValue = conditionalClauseId ? true : defaultValue;
                newExceptionValue = exceptionClauseId ? true : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.SentOnlyToMe = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfSentOnlyToMe = value;
                    }
                );
                break;
            case 'onTheToLine':
                originalConditionalValue = originalRule ? originalRule.MyNameInToBox : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfMyNameInToBox
                    : defaultValue;
                newConditionalValue = conditionalClauseId ? true : defaultValue;
                newExceptionValue = exceptionClauseId ? true : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.MyNameInToBox = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfMyNameInToBox = value;
                    }
                );
                break;
            case 'onTheCcLine':
                originalConditionalValue = originalRule ? originalRule.MyNameInCcBox : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfMyNameInCcBox
                    : defaultValue;
                newConditionalValue = conditionalClauseId ? true : defaultValue;
                newExceptionValue = exceptionClauseId ? true : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.MyNameInCcBox = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfMyNameInCcBox = value;
                    }
                );
                break;
            case 'notOnTheToLine':
                originalConditionalValue = originalRule
                    ? originalRule.MyNameNotInToBox
                    : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfMyNameNotInToBox
                    : defaultValue;
                newConditionalValue = conditionalClauseId ? true : defaultValue;
                newExceptionValue = exceptionClauseId ? true : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.MyNameNotInToBox = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfMyNameNotInToBox = value;
                    }
                );
                break;
            case 'subjectIncludes':
                originalConditionalValue = originalRule
                    ? originalRule.SubjectContainsWords
                    : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfSubjectContainsWords
                    : defaultValue;
                newConditionalValue = conditionalClauseId
                    ? <string[]>conditionalClauseValue
                    : defaultValue;
                newExceptionValue = exceptionClauseId
                    ? <string[]>exceptionClauseValue
                    : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.SubjectContainsWords = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfSubjectContainsWords = value;
                    }
                );
                break;
            case 'subjectOrBodyIncludes':
                originalConditionalValue = originalRule
                    ? originalRule.SubjectOrBodyContainsWords
                    : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfSubjectOrBodyContainsWords
                    : defaultValue;
                newConditionalValue = conditionalClauseId
                    ? <string[]>conditionalClauseValue
                    : defaultValue;
                newExceptionValue = exceptionClauseId
                    ? <string[]>exceptionClauseValue
                    : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.SubjectOrBodyContainsWords = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfSubjectOrBodyContainsWords = value;
                    }
                );
                break;
            case 'inTheBody':
                originalConditionalValue = originalRule
                    ? originalRule.BodyContainsWords
                    : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfBodyContainsWords
                    : defaultValue;
                newConditionalValue = conditionalClauseId
                    ? <string[]>conditionalClauseValue
                    : defaultValue;
                newExceptionValue = exceptionClauseId
                    ? <string[]>exceptionClauseValue
                    : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.BodyContainsWords = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfBodyContainsWords = value;
                    }
                );
                break;
            case 'inTheSenderAddress':
                originalConditionalValue = originalRule
                    ? originalRule.FromAddressContainsWords
                    : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfFromAddressContainsWords
                    : defaultValue;
                newConditionalValue = conditionalClauseId
                    ? <string[]>conditionalClauseValue
                    : defaultValue;
                newExceptionValue = exceptionClauseId
                    ? <string[]>exceptionClauseValue
                    : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.FromAddressContainsWords = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfFromAddressContainsWords = value;
                    }
                );
                break;
            case 'inTheRecipientAddress':
                originalConditionalValue = originalRule
                    ? originalRule.RecipientAddressContainsWords
                    : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfRecipientAddressContainsWords
                    : defaultValue;
                newConditionalValue = conditionalClauseId
                    ? <string[]>conditionalClauseValue
                    : defaultValue;
                newExceptionValue = exceptionClauseId
                    ? <string[]>exceptionClauseValue
                    : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.RecipientAddressContainsWords = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfRecipientAddressContainsWords = value;
                    }
                );
                break;
            case 'inTheMessageHeader':
                originalConditionalValue = originalRule
                    ? originalRule.HeaderContainsWords
                    : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfHeaderContainsWords
                    : defaultValue;
                newConditionalValue = conditionalClauseId
                    ? <string[]>conditionalClauseValue
                    : defaultValue;
                newExceptionValue = exceptionClauseId
                    ? <string[]>exceptionClauseValue
                    : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.HeaderContainsWords = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfHeaderContainsWords = value;
                    }
                );
                break;
            case 'withImportance':
                originalConditionalValue = originalRule
                    ? originalRule.WithImportance
                    : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfWithImportance
                    : defaultValue;
                newConditionalValue = conditionalClauseId
                    ? <NullableImportance>conditionalClauseValue
                    : defaultValue;
                newExceptionValue = exceptionClauseId
                    ? <NullableImportance>exceptionClauseValue
                    : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.WithImportance = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfWithImportance = value;
                    }
                );
                break;
            case 'sensitivity':
                originalConditionalValue = originalRule
                    ? originalRule.WithSensitivity
                    : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfWithSensitivity
                    : defaultValue;
                newConditionalValue = conditionalClauseId
                    ? <NullableSensitivity>conditionalClauseValue
                    : defaultValue;
                newExceptionValue = exceptionClauseId
                    ? <NullableSensitivity>exceptionClauseValue
                    : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.WithSensitivity = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfWithSensitivity = value;
                    }
                );
                break;
            case 'attachment':
                originalConditionalValue = originalRule ? originalRule.HasAttachment : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfHasAttachment
                    : defaultValue;
                newConditionalValue = conditionalClauseId ? true : defaultValue;
                newExceptionValue = exceptionClauseId ? true : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.HasAttachment = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfHasAttachment = value;
                    }
                );
                break;
            case 'flag':
                originalConditionalValue = originalRule
                    ? originalRule.RequestedAction
                    : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfRequestedAction
                    : defaultValue;
                newConditionalValue = conditionalClauseId
                    ? <NullableInboxRuleMessageFlag>conditionalClauseValue
                    : defaultValue;
                newExceptionValue = exceptionClauseId
                    ? <NullableInboxRuleMessageFlag>exceptionClauseValue
                    : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.RequestedAction = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfRequestedAction = value;
                    }
                );
                break;
            case 'type':
                originalConditionalValue = originalRule
                    ? originalRule.MessageTypeMatches
                    : defaultValue;
                originalExceptionValue = originalRule
                    ? originalRule.ExceptIfMessageTypeMatches
                    : defaultValue;
                newConditionalValue = conditionalClauseId
                    ? <NullableInboxRuleMessageType>conditionalClauseValue
                    : defaultValue;
                newExceptionValue = exceptionClauseId
                    ? <NullableInboxRuleMessageType>exceptionClauseValue
                    : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.MessageTypeMatches = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfMessageTypeMatches = value;
                    }
                );
                break;
            case 'atLeast':
                originalConditionalValue =
                    originalRule && originalRule.WithinSizeRangeMinimum
                        ? originalRule.WithinSizeRangeMinimum
                        : defaultValue;
                originalExceptionValue =
                    originalRule && originalRule.ExceptIfWithinSizeRangeMinimum
                        ? originalRule.ExceptIfWithinSizeRangeMinimum
                        : defaultValue;
                newConditionalValue = getClauseValueWithFallback(
                    <number>conditionalClauseValue,
                    mapKilobytesToBytes,
                    defaultValue
                );
                newExceptionValue = getClauseValueWithFallback(
                    <number>exceptionClauseValue,
                    mapKilobytesToBytes,
                    defaultValue
                );

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.WithinSizeRangeMinimum = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfWithinSizeRangeMinimum = value;
                    }
                );
                break;
            case 'atMost':
                originalConditionalValue =
                    originalRule && originalRule.WithinSizeRangeMaximum
                        ? originalRule.WithinSizeRangeMaximum
                        : defaultValue;
                originalExceptionValue =
                    originalRule && originalRule.ExceptIfWithinSizeRangeMaximum
                        ? originalRule.ExceptIfWithinSizeRangeMaximum
                        : defaultValue;
                newConditionalValue = getClauseValueWithFallback(
                    <number>conditionalClauseValue,
                    mapKilobytesToBytes,
                    defaultValue
                );
                newExceptionValue = getClauseValueWithFallback(
                    <number>exceptionClauseValue,
                    mapKilobytesToBytes,
                    defaultValue
                );

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.WithinSizeRangeMaximum = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfWithinSizeRangeMaximum = value;
                    }
                );
                break;
            case 'before':
                originalConditionalValue =
                    originalRule && originalRule.ReceivedBeforeDate
                        ? originalRule.ReceivedBeforeDate
                        : defaultValue;
                originalExceptionValue =
                    originalRule && originalRule.ExceptIfReceivedBeforeDate
                        ? originalRule.ExceptIfReceivedBeforeDate
                        : defaultValue;
                newConditionalValue = conditionalClauseValue
                    ? <string>conditionalClauseValue
                    : defaultValue;
                newExceptionValue = exceptionClauseValue
                    ? <string>exceptionClauseValue
                    : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.ReceivedBeforeDate = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalExceptionValue, newExceptionValue),
                    value => {
                        inboxRule.ExceptIfReceivedBeforeDate = value;
                    }
                );
                break;
            case 'after':
                originalConditionalValue =
                    originalRule && originalRule.ReceivedAfterDate
                        ? originalRule.ReceivedAfterDate
                        : defaultValue;
                originalExceptionValue =
                    originalRule && originalRule.ExceptIfReceivedAfterDate
                        ? originalRule.ExceptIfReceivedAfterDate
                        : defaultValue;
                newConditionalValue = conditionalClauseValue
                    ? <string>conditionalClauseValue
                    : defaultValue;
                newExceptionValue = exceptionClauseValue
                    ? <string>exceptionClauseValue
                    : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newConditionalValue,
                    originalConditionalValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.ReceivedAfterDate = value;
                    }
                );

                // Set exception value
                setClauseValueWithFallback(
                    newExceptionValue,
                    originalExceptionValue,
                    defaultValue,
                    areClauseValuesEqual(originalConditionalValue, newConditionalValue),
                    value => {
                        inboxRule.ExceptIfReceivedAfterDate = value;
                    }
                );
                break;
            // Do nothing for any case (server type doesn't have an any-specific field)
            case 'any':
                break;
            default:
                throw new Error(
                    'Cannot map an exception inbox rule clause to an inbox rule property for a clause type that does not exist'
                );
        }
    }

    // map action clauses
    for (let actionClauseType of getActionClauseTypeOptions()) {
        let actionClauseId = actionExistingClauseTypesMap.get(actionClauseType);
        let actionClauseValue = actionClauseId ? clauseMap.get(actionClauseId).clauseValue : null;
        let defaultValue = getDefaultInboxRuleClauseValue(actionClauseType);
        let originalValue;
        let newValue;

        switch (actionClauseType) {
            case 'moveTo':
                newValue = defaultValue;
                originalValue = originalRule ? originalRule.MoveToFolder : defaultValue;
                let newMoveToFolderId = defaultValue;
                let moveToFolder = actionClauseValue as InboxRuleFolderType;
                if (moveToFolder) {
                    newMoveToFolderId = moveToFolder.folderId;
                    newValue = {
                        DisplayName: moveToFolder.displayName,
                        RawIdentity: newMoveToFolderId,
                    };
                }
                let originalRuleMoveToFolderId = originalValue
                    ? getFolderFromRawId(originalRule.MoveToFolder.RawIdentity).folderId
                    : null;

                setClauseValueWithFallback(
                    newValue,
                    originalValue,
                    defaultValue,
                    areClauseValuesEqual(originalRuleMoveToFolderId, newMoveToFolderId),
                    value => {
                        inboxRule.MoveToFolder = value;
                    }
                );
                break;
            case 'copyTo':
                newValue = defaultValue;
                originalValue = originalRule ? originalRule.CopyToFolder : defaultValue;
                let newCopyToFolderId = defaultValue;
                let copyToFolder = actionClauseValue as InboxRuleFolderType;
                if (copyToFolder) {
                    newCopyToFolderId = copyToFolder.folderId;
                    newValue = {
                        DisplayName: copyToFolder.displayName,
                        RawIdentity: newCopyToFolderId,
                    };
                }
                let originalRuleCopyToFolderId = originalValue
                    ? getFolderFromRawId(originalRule.CopyToFolder.RawIdentity).folderId
                    : null;

                setClauseValueWithFallback(
                    newValue,
                    originalValue,
                    defaultValue,
                    areClauseValuesEqual(originalRuleCopyToFolderId, newCopyToFolderId),
                    value => {
                        inboxRule.CopyToFolder = value;
                    }
                );
                break;
            case 'delete':
                originalValue = originalRule ? originalRule.DeleteMessage : defaultValue;
                newValue = actionClauseId ? true : defaultValue;

                setClauseValueWithFallback(
                    newValue,
                    originalValue,
                    defaultValue,
                    areClauseValuesEqual(originalValue, newValue),
                    value => {
                        inboxRule.DeleteMessage = value;
                    }
                );
                break;
            case 'pin':
                originalValue = originalRule ? originalRule.PinMessage : defaultValue;
                newValue = actionClauseId ? true : defaultValue;

                setClauseValueWithFallback(
                    newValue,
                    originalValue,
                    defaultValue,
                    areClauseValuesEqual(originalValue, newValue),
                    value => {
                        inboxRule.PinMessage = value;
                    }
                );
                break;
            case 'read':
                originalValue = originalRule ? originalRule.MarkAsRead : defaultValue;
                newValue = actionClauseId ? true : defaultValue;

                setClauseValueWithFallback(
                    newValue,
                    originalValue,
                    defaultValue,
                    areClauseValuesEqual(originalValue, newValue),
                    value => {
                        inboxRule.MarkAsRead = value;
                    }
                );
                break;
            case 'junk':
                // Mark as junk is a special case because there is no server property for it. We give the user the option and do a
                // Move to folder 'Junk Email' under the covers.
                let junkFolderId = folderNameToId('junkemail');
                let newJunkClauseValue = actionClauseId
                    ? {
                          DisplayName: store.junkFolderDisplayName,
                          RawIdentity: junkFolderId,
                      }
                    : defaultValue;
                let originalMoveToFolderId =
                    originalRule && originalRule.MoveToFolder
                        ? getFolderFromRawId(originalRule.MoveToFolder.RawIdentity).folderId
                        : defaultValue;
                let areMoveToAndJunkFoldersEquivalent = originalMoveToFolderId
                    ? newJunkClauseValue && originalMoveToFolderId === junkFolderId
                    : false;

                // Since junk is special, we have to do an additional check if the move to folder on the original rule was a junk folder
                if (newJunkClauseValue && (!originalRule || !areMoveToAndJunkFoldersEquivalent)) {
                    inboxRule.MoveToFolder = newJunkClauseValue;
                } else if (
                    !newJunkClauseValue &&
                    (areMoveToAndJunkFoldersEquivalent &&
                        originalRule.MoveToFolder !== defaultValue)
                ) {
                    inboxRule.MoveToFolder = defaultValue;
                }
                break;
            case 'markImportance':
                originalValue = originalRule ? originalRule.MarkImportance : defaultValue;
                newValue = actionClauseId ? <NullableImportance>actionClauseValue : null;

                setClauseValueWithFallback(
                    newValue,
                    originalValue,
                    defaultValue,
                    areClauseValuesEqual(originalValue, newValue),
                    value => {
                        inboxRule.MarkImportance = value;
                    }
                );

                break;
            case 'category':
                originalValue = originalRule ? originalRule.ApplyCategory : defaultValue;
                newValue = actionClauseId ? <string[]>actionClauseValue : defaultValue;

                // Set conditional value
                setClauseValueWithFallback(
                    newValue,
                    originalValue,
                    defaultValue,
                    areClauseValuesEqual(originalValue, newValue),
                    value => {
                        inboxRule.ApplyCategory = value;
                    }
                );
                break;
            case 'forwardTo':
                originalValue = originalRule ? originalRule.ForwardTo : defaultValue;
                newValue = getClauseValueWithFallback(
                    actionClauseValue,
                    mapReadWriteRecipientWellViewStateToPeopleIdentity,
                    defaultValue
                );

                setClauseValueWithFallback(
                    newValue,
                    originalValue,
                    defaultValue,
                    areClauseValuesEqual(originalValue, newValue),
                    value => {
                        inboxRule.ForwardTo = value;
                    }
                );
                break;
            case 'forwardAsAttachmentTo':
                originalValue = originalRule ? originalRule.ForwardAsAttachmentTo : defaultValue;
                newValue = getClauseValueWithFallback(
                    actionClauseValue,
                    mapReadWriteRecipientWellViewStateToPeopleIdentity,
                    defaultValue
                );

                setClauseValueWithFallback(
                    newValue,
                    originalValue,
                    defaultValue,
                    areClauseValuesEqual(originalValue, newValue),
                    value => {
                        inboxRule.ForwardAsAttachmentTo = value;
                    }
                );
                break;
            case 'redirectTo':
                originalValue = originalRule ? originalRule.RedirectTo : defaultValue;
                newValue = getClauseValueWithFallback(
                    actionClauseValue,
                    mapReadWriteRecipientWellViewStateToPeopleIdentity,
                    defaultValue
                );

                setClauseValueWithFallback(
                    newValue,
                    originalValue,
                    defaultValue,
                    areClauseValuesEqual(originalValue, newValue),
                    value => {
                        inboxRule.RedirectTo = value;
                    }
                );
                break;
            default:
                throw new Error(
                    'Cannot map an exception inbox rule clause to an inbox rule property for a clause type that does not exist'
                );
        }
    }

    inboxRule.StopProcessingRules = store.inboxRulesEditState.ignoreSubsequentRules;

    if (store.inboxRulesEditState.ruleId) {
        inboxRule.Identity = {
            DisplayName: store.inboxRulesEditState.ruleName,
            RawIdentity: store.inboxRulesEditState.ruleId,
        };
    }

    return inboxRule;
}

// Set inbox rule value to the edit state value if:
// 1) There is an edit state value (user added that clause type) AND
// 2) It is a new rule (no original rule) OR the value of the new rule is different than value of the original rule

// Else if:
// Set inbox rule value to the default inbox rule value if user removes the clause while editing:
// 1) There is no edit state value AND
// 2) There is an original rule (in edit mode) AND the value of the original rule was not the default value
function setClauseValueWithFallback(
    newValue: ClauseValueType,
    originalValue: any,
    defaultValue: any,
    areClauseValuesEqual: boolean,
    valueSetter: (value: any) => void
) {
    if (!areClauseValuesEqual) {
        let valueToSet;
        if (newValue && newValue != originalValue) {
            valueToSet = newValue;
        } else if (!newValue && originalValue != defaultValue) {
            valueToSet = defaultValue;
        }

        valueSetter(valueToSet);
    }
}

function areClauseValuesEqual(originalValue: any, newValue: any) {
    return originalValue === newValue;
}

function getClauseValueWithFallback(
    existingValue: ClauseValueType,
    mapFunction: (value: ClauseValueType) => any,
    defaultValue: any
) {
    return existingValue ? mapFunction(existingValue) : defaultValue;
}

function mapKilobytesToBytes(clauseValue: number) {
    return clauseValue * KB_TO_B;
}
